import { useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { appointmentsAPI } from '../../api'
import {
  format, parseISO, startOfMonth, endOfMonth, eachDayOfInterval,
  startOfWeek, endOfWeek, isSameMonth, isSameDay, isToday, addMonths, subMonths,
} from 'date-fns'
import DatePicker from 'react-datepicker'
import {
  RiSearchLine, RiCheckLine, RiCloseLine, RiLoader4Line,
  RiArrowLeftSLine, RiArrowRightSLine, RiCalendarLine, RiListUnordered,
} from 'react-icons/ri'
import toast from 'react-hot-toast'
import 'react-datepicker/dist/react-datepicker.css'

// Unwrap appointments out of either a Laravel paginator or a plain array.
// Backend may respond as { data: [...] } OR { data: { data: [...], current_page, ... } }.
const extractAppointments = (r) => {
  const body = r?.data?.data ?? r?.data
  if (Array.isArray(body)) return body
  if (Array.isArray(body?.data)) return body.data
  return []
}

const STATUS_OPTS = ['all', 'pending', 'confirmed', 'completed', 'cancelled']
const STATUS_COLORS = {
  pending:   'bg-amber-100 text-amber-700',
  confirmed: 'bg-green-100 text-green-700',
  completed: 'bg-blue-100 text-blue-700',
  cancelled: 'bg-red-100 text-red-700',
  no_show:   'bg-gray-200 text-gray-600',
}
const STATUS_DOT = {
  pending:   'bg-amber-400',
  confirmed: 'bg-green-500',
  completed: 'bg-blue-500',
  cancelled: 'bg-red-400',
  no_show:   'bg-gray-400',
}

export default function ManageAppointments() {
  const [view, setView] = useState('calendar')

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <h2 className="font-display text-salon-dark text-2xl font-bold">Appointments</h2>

        <div className="inline-flex bg-salon-cream rounded-full p-1">
          <button
            onClick={() => setView('calendar')}
            className={`px-4 py-1.5 rounded-full text-sm font-semibold font-body inline-flex items-center gap-1.5 transition-all ${
              view === 'calendar' ? 'bg-salon-primary text-white shadow-salon-sm' : 'text-salon-muted hover:text-salon-primary'
            }`}
          >
            <RiCalendarLine /> Calendar
          </button>
          <button
            onClick={() => setView('list')}
            className={`px-4 py-1.5 rounded-full text-sm font-semibold font-body inline-flex items-center gap-1.5 transition-all ${
              view === 'list' ? 'bg-salon-primary text-white shadow-salon-sm' : 'text-salon-muted hover:text-salon-primary'
            }`}
          >
            <RiListUnordered /> List
          </button>
        </div>
      </div>

      {view === 'calendar' ? <CalendarView /> : <ListView />}
    </div>
  )
}

/* ─── Calendar view ────────────────────────────────────────── */
function CalendarView() {
  const qc = useQueryClient()
  const [cursor, setCursor]   = useState(new Date())
  const [selected, setSelected] = useState(new Date())

  const monthStart = startOfMonth(cursor)
  const monthEnd   = endOfMonth(cursor)
  const gridStart  = startOfWeek(monthStart,   { weekStartsOn: 1 })
  const gridEnd    = endOfWeek(monthEnd,       { weekStartsOn: 1 })
  const days       = eachDayOfInterval({ start: gridStart, end: gridEnd })

  // Pull all appointments overlapping the visible grid. We over-fetch (per_page big)
  // because the admin month grid rarely exceeds a few hundred bookings.
  const { data, isLoading } = useQuery({
    queryKey: ['admin-appointments-month', format(monthStart, 'yyyy-MM')],
    queryFn:  () => appointmentsAPI.getAll({
      from: format(gridStart, 'yyyy-MM-dd'),
      to:   format(gridEnd,   'yyyy-MM-dd'),
      per_page: 500,
    }),
    select: extractAppointments,
  })

  const appointments = data || []

  const byDay = useMemo(() => {
    const m = new Map()
    for (const a of appointments) {
      // Backend may return either `appointment_date` (raw column) or `date` (transformer).
      const raw = a.appointment_date || a.date
      if (!raw) continue
      const key = format(typeof raw === 'string' ? parseISO(raw) : new Date(raw), 'yyyy-MM-dd')
      if (!m.has(key)) m.set(key, [])
      m.get(key).push(a)
    }
    return m
  }, [appointments])

  const updateMut = useMutation({
    mutationFn: ({ id, payload }) => appointmentsAPI.update(id, payload),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['admin-appointments-month'] }); toast.success('Updated.') },
    onError:   () => toast.error('Update failed.'),
  })

  const dayAppts = byDay.get(format(selected, 'yyyy-MM-dd')) || []

  return (
    <div className="grid lg:grid-cols-[1fr_360px] gap-6">
      {/* Calendar grid */}
      <div className="bg-white rounded-2xl border border-salon-cream-dark overflow-hidden">
        {/* Month bar */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-salon-cream-dark">
          <button
            onClick={() => setCursor(subMonths(cursor, 1))}
            className="w-9 h-9 rounded-full hover:bg-salon-cream flex items-center justify-center text-salon-muted hover:text-salon-primary transition-colors"
            aria-label="Previous month"
          >
            <RiArrowLeftSLine className="text-xl" />
          </button>
          <div className="text-center">
            <p className="font-display text-salon-dark text-lg font-bold">{format(cursor, 'MMMM yyyy')}</p>
            <button
              onClick={() => { setCursor(new Date()); setSelected(new Date()) }}
              className="text-xs text-salon-primary hover:underline font-body"
            >
              Today
            </button>
          </div>
          <button
            onClick={() => setCursor(addMonths(cursor, 1))}
            className="w-9 h-9 rounded-full hover:bg-salon-cream flex items-center justify-center text-salon-muted hover:text-salon-primary transition-colors"
            aria-label="Next month"
          >
            <RiArrowRightSLine className="text-xl" />
          </button>
        </div>

        {/* Weekday header */}
        <div className="grid grid-cols-7 text-center text-xs font-semibold uppercase tracking-wide text-salon-muted bg-salon-cream/40">
          {['Mon','Tue','Wed','Thu','Fri','Sat','Sun'].map((d) => (
            <div key={d} className="py-2 font-body">{d}</div>
          ))}
        </div>

        {/* Day cells */}
        <div className="grid grid-cols-7 border-t border-salon-cream-dark">
          {days.map((day) => {
            const key      = format(day, 'yyyy-MM-dd')
            const dayList  = byDay.get(key) || []
            const inMonth  = isSameMonth(day, cursor)
            const isSel    = isSameDay(day, selected)
            const isCur    = isToday(day)

            return (
              <button
                key={key}
                onClick={() => setSelected(day)}
                className={`min-h-[88px] text-left px-2 py-2 border-b border-r border-salon-cream-dark
                            transition-colors group relative
                            ${isSel ? 'bg-salon-primary/5 ring-2 ring-inset ring-salon-primary' : 'hover:bg-salon-cream/50'}
                            ${!inMonth ? 'bg-salon-cream/30' : ''}`}
              >
                <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-semibold font-body
                                  ${isCur
                                      ? 'bg-salon-primary text-white'
                                      : inMonth ? 'text-salon-dark' : 'text-salon-muted/50'}`}>
                  {format(day, 'd')}
                </span>

                {dayList.length > 0 && (
                  <div className="mt-1 space-y-1">
                    {dayList.slice(0, 3).map((a) => (
                      <div key={a.id} className="flex items-center gap-1.5 truncate">
                        <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${STATUS_DOT[a.status] || 'bg-salon-primary'}`} />
                        <span className="truncate text-[11px] text-salon-dark font-body">
                          {(a.appointment_time || a.time || '').slice(0,5)}{' '}
                          {a.user?.name || a.customer_name || 'Guest'}
                        </span>
                      </div>
                    ))}
                    {dayList.length > 3 && (
                      <p className="text-[11px] text-salon-muted font-body">+{dayList.length - 3} more</p>
                    )}
                  </div>
                )}
              </button>
            )
          })}
        </div>

        {isLoading && (
          <div className="flex justify-center py-3 text-salon-muted">
            <RiLoader4Line className="animate-spin text-xl text-salon-primary" />
          </div>
        )}
      </div>

      {/* Selected-day pane */}
      <aside className="bg-white rounded-2xl border border-salon-cream-dark p-5">
        <p className="text-salon-muted text-xs font-body uppercase tracking-widest mb-1">Selected day</p>
        <h3 className="font-display text-salon-dark text-xl font-bold mb-1">{format(selected, 'EEEE')}</h3>
        <p className="text-salon-muted font-body text-sm mb-5">{format(selected, 'd MMMM yyyy')}</p>

        {dayAppts.length === 0 ? (
          <div className="py-8 text-center">
            <RiCalendarLine className="text-4xl text-salon-cream-dark mx-auto mb-2" />
            <p className="text-salon-muted font-body text-sm">No appointments scheduled.</p>
          </div>
        ) : (
          <ul className="space-y-3">
            {dayAppts
              .slice()
              .sort((a, b) => (a.appointment_time || a.time || '').localeCompare(b.appointment_time || b.time || ''))
              .map((a) => (
              <li key={a.id} className="rounded-xl border border-salon-cream-dark p-3 hover:border-salon-primary/40 transition-colors">
                <div className="flex items-start justify-between gap-2 mb-1">
                  <div>
                    <p className="font-display font-semibold text-salon-dark text-sm">
                      {(a.appointment_time || a.time || '—').slice(0,5)}
                    </p>
                    <p className="text-salon-dark font-body text-sm">
                      {a.user?.name || a.customer_name || 'Guest'}
                    </p>
                  </div>
                  <span className={`badge capitalize text-[10px] ${STATUS_COLORS[a.status] || 'bg-gray-100 text-gray-600'}`}>
                    {a.status?.replace('_',' ')}
                  </span>
                </div>
                <p className="text-salon-muted font-body text-xs truncate">
                  {a.services?.map(s => s.name).join(', ') || '—'}
                </p>
                <p className="text-salon-muted font-body text-xs mt-0.5">
                  {a.staff?.name ? `with ${a.staff.name}` : 'No stylist assigned'}
                  {(a.total_amount || a.total) ? ` · ₹${parseFloat(a.total_amount || a.total).toLocaleString()}` : ''}
                </p>
                {a.status === 'pending' && (
                  <div className="flex gap-1 mt-2">
                    <button onClick={() => updateMut.mutate({ id: a.id, payload: { status: 'confirmed' } })}
                      className="px-2.5 py-1 rounded-lg bg-green-100 text-green-600 text-xs font-semibold hover:bg-green-200">
                      Confirm
                    </button>
                    <button onClick={() => updateMut.mutate({ id: a.id, payload: { status: 'cancelled' } })}
                      className="px-2.5 py-1 rounded-lg bg-red-100 text-red-500 text-xs font-semibold hover:bg-red-200">
                      Cancel
                    </button>
                  </div>
                )}
                {a.status === 'confirmed' && (
                  <button onClick={() => updateMut.mutate({ id: a.id, payload: { status: 'completed' } })}
                    className="mt-2 px-2.5 py-1 rounded-lg bg-blue-100 text-blue-600 text-xs font-semibold hover:bg-blue-200">
                    Mark complete
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </aside>
    </div>
  )
}

/* ─── List view (legacy, retained) ─────────────────────────── */
function ListView() {
  const qc = useQueryClient()
  const [search,     setSearch]     = useState('')
  const [status,     setStatus]     = useState('all')
  const [dateFilter, setDateFilter] = useState(null)
  const [page,       setPage]       = useState(1)

  const { data, isLoading } = useQuery({
    queryKey: ['admin-appointments-list', search, status, dateFilter, page],
    queryFn: () => appointmentsAPI.getAll({
      search,
      status: status !== 'all' ? status : undefined,
      date:   dateFilter ? format(dateFilter, 'yyyy-MM-dd') : undefined,
      page,
    }),
    select: (r) => {
      const body = r.data?.data ?? r.data
      if (Array.isArray(body)) return { items: body, meta: {} }
      return { items: Array.isArray(body?.data) ? body.data : [], meta: body || {} }
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }) => appointmentsAPI.update(id, payload),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['admin-appointments-list'] }); toast.success('Updated.') },
    onError:   () => toast.error('Update failed.'),
  })
  const handleStatus = (id, newStatus) => updateMutation.mutate({ id, payload: { status: newStatus } })

  const appointments = data?.items || []
  const meta         = data?.meta  || {}

  return (
    <>
      {/* Filters */}
      <div className="bg-white rounded-2xl border border-salon-cream-dark p-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <RiSearchLine className="absolute left-3 top-1/2 -translate-y-1/2 text-salon-muted" />
          <input value={search} onChange={(e) => { setSearch(e.target.value); setPage(1) }}
            className="input-field pl-9 py-2 text-sm" placeholder="Search by name, phone, email…" />
        </div>

        <div className="flex gap-1 flex-wrap">
          {STATUS_OPTS.map((s) => (
            <button key={s} onClick={() => { setStatus(s); setPage(1) }}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold font-body capitalize transition-all ${
                status === s ? 'bg-salon-primary text-white' : 'bg-salon-cream text-salon-muted hover:text-salon-primary'
              }`}
            >{s}</button>
          ))}
        </div>

        <DatePicker selected={dateFilter} onChange={setDateFilter} isClearable
          placeholderText="Filter by date" className="input-field py-2 text-sm w-36" />
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-salon-cream-dark overflow-hidden mt-5">
        {isLoading ? (
          <div className="flex justify-center py-20"><RiLoader4Line className="animate-spin text-3xl text-salon-primary" /></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm font-body">
              <thead>
                <tr className="bg-salon-cream text-salon-muted text-xs font-semibold uppercase tracking-wider">
                  <th className="text-left px-5 py-3">#</th>
                  <th className="text-left px-5 py-3">Customer</th>
                  <th className="text-left px-5 py-3">Services</th>
                  <th className="text-left px-5 py-3">Date &amp; Time</th>
                  <th className="text-left px-5 py-3">Stylist</th>
                  <th className="text-left px-5 py-3">Total</th>
                  <th className="text-left px-5 py-3">Status</th>
                  <th className="text-left px-5 py-3">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-salon-cream-dark">
                {appointments.length === 0 ? (
                  <tr><td colSpan={8} className="text-center py-16 text-salon-muted">No appointments found</td></tr>
                ) : appointments.map((a) => (
                  <tr key={a.id} className="hover:bg-salon-cream/30 transition-colors">
                    <td className="px-5 py-3.5 text-salon-muted">#{a.id}</td>
                    <td className="px-5 py-3.5">
                      <p className="font-medium text-salon-dark">{a.user?.name || a.customer_name}</p>
                      <p className="text-xs text-salon-muted">{a.user?.phone || a.customer_phone}</p>
                    </td>
                    <td className="px-5 py-3.5 text-salon-dark max-w-[140px] truncate">
                      {a.services?.map((s) => s.name).join(', ') || '–'}
                    </td>
                    <td className="px-5 py-3.5">
                      <p className="text-salon-dark">{a.appointment_date || a.date}</p>
                      <p className="text-xs text-salon-muted">{(a.appointment_time || a.time || '').slice(0,5)}</p>
                    </td>
                    <td className="px-5 py-3.5 text-salon-dark">{a.staff?.name || '–'}</td>
                    <td className="px-5 py-3.5 text-salon-secondary font-semibold">₹{parseFloat(a.total_amount || a.total || 0).toLocaleString()}</td>
                    <td className="px-5 py-3.5">
                      <span className={`badge capitalize ${STATUS_COLORS[a.status] || 'bg-gray-100 text-gray-600'}`}>
                        {a.status?.replace('_',' ')}
                      </span>
                    </td>
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-1">
                        {a.status === 'pending' && (
                          <>
                            <button onClick={() => handleStatus(a.id, 'confirmed')}
                              className="p-1.5 rounded-lg bg-green-100 text-green-600 hover:bg-green-200" title="Confirm">
                              <RiCheckLine />
                            </button>
                            <button onClick={() => handleStatus(a.id, 'cancelled')}
                              className="p-1.5 rounded-lg bg-red-100 text-red-500 hover:bg-red-200" title="Cancel">
                              <RiCloseLine />
                            </button>
                          </>
                        )}
                        {a.status === 'confirmed' && (
                          <button onClick={() => handleStatus(a.id, 'completed')}
                            className="px-2.5 py-1 rounded-lg bg-blue-100 text-blue-600 text-xs font-semibold hover:bg-blue-200">
                            Complete
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {meta.last_page > 1 && (
          <div className="flex items-center justify-between px-5 py-4 border-t border-salon-cream-dark">
            <p className="text-xs text-salon-muted font-body">
              Showing {meta.from}–{meta.to} of {meta.total}
            </p>
            <div className="flex gap-1">
              <button disabled={page === 1} onClick={() => setPage(page - 1)}
                className="px-3 py-1.5 rounded-lg text-xs font-semibold font-body bg-salon-cream text-salon-muted disabled:opacity-40 hover:text-salon-primary">
                Prev
              </button>
              <button disabled={page === meta.last_page} onClick={() => setPage(page + 1)}
                className="px-3 py-1.5 rounded-lg text-xs font-semibold font-body bg-salon-cream text-salon-muted disabled:opacity-40 hover:text-salon-primary">
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  )
}
